import {
  Box,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { readAchievementStatistics, readActivity, readStatistics } from '../api/endpoints';
import type { AchievementStatistics, PlatformActivity, PlatformStatistics } from '../api/types';
import { ChartCard } from '../components/ChartCard';
import { children } from '../components/slovakPlural';
import { useChartTheme } from '../components/useChartTheme';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageHeader } from '../components/PageHeader';
import { StatTile } from '../components/StatTile';
import { CHART_SERIES } from '../theme/palette';

const PERIODS = [7, 30, 90];

/** `2026-08-26` reads as `26.8.` on an axis — the year is the same for every tick and costs width. */
const shortDay = (day: string): string => {
  const [, month, date] = day.split('-');

  return `${Number(date)}.${Number(month)}.`;
};

const asPercentage = (value: number): string => `${Math.round(value * 100)} %`;

export const DashboardPage = () => {
  const theme = useTheme();
  const [days, setDays] = useState<number>(30);
  const chartTheme = useChartTheme();
  const { data, error, isLoading, refetch } = useQuery<PlatformStatistics>({
    queryKey: ['statistics', days],
    queryFn: () => readStatistics(days),
  });
  const {
    data: activity,
    error: activityError,
    refetch: refetchActivity,
  } = useQuery<PlatformActivity>({
    queryKey: ['activity', days],
    queryFn: () => readActivity(days),
  });
  // One tile, not a section. The dashboard says whether achievements are being earned at all; whether the
  // achievements themselves are any good is a whole page, and it is a different question.
  const achievements = useQuery<AchievementStatistics>({
    queryKey: ['achievement-statistics', days],
    queryFn: () => readAchievementStatistics(days),
  });

  const totals = data?.totals;
  const content = data?.content;
  const chart = totals
    ? [
        { name: 'Začaté', value: totals.sessionsStarted },
        { name: 'Dokončené', value: totals.sessionsCompleted },
        { name: 'Odpovede', value: totals.attempts },
        { name: 'Uzavreté úlohy', value: totals.instancesClosed },
      ]
    : [];

  return (
    <>
      <PageHeader
        title="Prehľad"
        subtitle="Ako sa platforma používa a či obsah stíha tempo. Všetky čísla sú za zvolené obdobie okrem stavových počtov, ktoré sú aktuálne."
        action={
          <TextField
            select
            label="Obdobie"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            sx={{ minWidth: 176 }}
          >
            {PERIODS.map((option) => (
              <MenuItem key={option} value={option}>
                Posledných {option} dní
              </MenuItem>
            ))}
          </TextField>
        }
      />

      {/* One notice covers two requests, so trying again has to cover both. */}
      <ErrorNotice
        error={error ?? activityError}
        onRetry={() => {
          void refetch();
          void refetchActivity();
        }}
      />

      {isLoading || !totals || !content ? (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Skeleton variant="rounded" height={116} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Rodičovské účty"
                value={totals.accounts}
                hint={`+${totals.accountsCreatedInPeriod} za obdobie`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Aktívne deti" value={totals.activeChildren} hint="nearchivované profily" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Dokončenosť tréningov"
                value={`${Math.round(totals.completionRate * 100)} %`}
                hint={`${totals.sessionsCompleted} z ${totals.sessionsStarted}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Priemerná úroveň"
                value={totals.averageSubskillLevel}
                hint="naprieč všetkými podschopnosťami"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Odpovede" value={totals.attempts} hint="za obdobie" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Udelené ocenenia"
                value={achievements.data?.totals.earnedInPeriod ?? '—'}
                hint={
                  achievements.data
                    ? `${children(achievements.data.totals.childrenWithAny)} má aspoň jedno`
                    : 'za obdobie'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Uzavreté úlohy" value={totals.instancesClosed} hint="za obdobie" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Úlohy v katalógu"
                value={`${content.enabledChallenges}/${content.registeredChallenges}`}
                hint="zapnuté / registrované"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Príbehy"
                value={content.publishedStories}
                hint={`${content.storyQuestions} otázok spolu`}
              />
            </Grid>
          </Grid>

          <ChartCard
            title="Denná aktivita"
            description="Každý deň obdobia vrátane tých prázdnych — graf poskladaný len z dní, ktoré majú záznamy, si medzery zaceľuje sám a ukazuje rovnú čiaru tam, kde sa aplikácia nepoužívala."
          >
            <AreaChart data={activity?.daily ?? []} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
              <CartesianGrid {...chartTheme.grid} />
              <XAxis dataKey="day" tickFormatter={shortDay} {...chartTheme.axis} />
              <YAxis allowDecimals={false} {...chartTheme.axis} />
              <Tooltip {...chartTheme.tooltip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="sessionsStarted"
                name="Začaté tréningy"
                stroke={CHART_SERIES[0]}
                fill={CHART_SERIES[0]}
                fillOpacity={0.18}
              />
              <Area
                type="monotone"
                dataKey="sessionsCompleted"
                name="Dokončené"
                stroke={CHART_SERIES[1]}
                fill={CHART_SERIES[1]}
                fillOpacity={0.18}
              />
            </AreaChart>
          </ChartCard>

          <ChartCard
            title="Úspešnosť odpovedí"
            description="Podiel správnych odpovedí za deň. Prudký pokles znamená buď zle nastavenú náročnosť, alebo úlohu, ktorá je nezrozumiteľná — nie deti, ktoré sa zhoršili."
            height={224}
          >
            <LineChart data={activity?.daily ?? []} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
              <CartesianGrid {...chartTheme.grid} />
              <XAxis dataKey="day" tickFormatter={shortDay} {...chartTheme.axis} />
              <YAxis domain={[0, 1]} tickFormatter={asPercentage} {...chartTheme.axis} />
              <Tooltip {...chartTheme.tooltip} formatter={(value: number) => asPercentage(value)} />
              <Line
                type="monotone"
                dataKey="accuracy"
                name="Úspešnosť"
                stroke={CHART_SERIES[2]}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ChartCard>

          <Card variant="outlined" sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6">Objem za obdobie</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Rozdiel medzi začatými a dokončenými tréningami ukazuje, koľko detí prestane v polovici.
              </Typography>
              <Box sx={{ height: 296 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chart} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="name"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                    />
                    <Tooltip
                      cursor={{ fill: theme.palette.action.hover }}
                      contentStyle={{
                        background: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 10,
                        color: theme.palette.text.primary,
                      }}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={82}>
                      {chart.map((entry, index) => (
                        <Cell key={entry.name} fill={CHART_SERIES[index % CHART_SERIES.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
};
